import React from 'react';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DateField as RefineDataField } from '@refinedev/mui';
import { Typography, Chip, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { type ParticipantProfile } from '@aphp-ema/common/participant';
import { ParticipantActions } from './ParticipantActions';

export const useParticipantListColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'emaId',
        headerName: 'EMA ID',
        minWidth: 20,
        sortable: false,
        filterable: false,
      },
      {
        field: 'email',
        headerName: t('pages.participants.fields.email'),
        minWidth: 200,
        flex: 1,
        display: 'flex',
        sortable: false,
        filterable: false,
      },
      {
        field: 'phone',
        headerName: t('pages.participants.fields.phone'),
        minWidth: 150,
        flex: 1,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<ParticipantProfile, string | null>,
        ) {
          if (!params.value) return '-';
          return <Typography component="p">{params.value}</Typography>;
        },
        sortable: false,
        filterable: false,
      },
      {
        field: 'patientData',
        headerName: t('pages.participants.fields.chronicIllnesses'),
        minWidth: 150,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<
            ParticipantProfile,
            ParticipantProfile['patientData']
          >,
        ) {
          const chronicIllnesses = params.value?.chronicIllnesses;
          if (!chronicIllnesses || chronicIllnesses.length === 0) return '-';

          return (
            <Box>
              <Chip
                label={
                  chronicIllnesses.length +
                  ' ' +
                  t('pages.participants.fields.chronicIllnesses')
                }
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          );
        },
        sortable: false,
        filterable: false,
      },
      {
        field: 'createdAt',
        headerName: t('pages.participants.fields.createdAt'),
        minWidth: 120,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<
            ParticipantProfile,
            string | Date | null
          >,
        ) {
          return <RefineDataField value={params.value} />;
        },
        sortable: false,
        filterable: false,
      },
      {
        field: 'actions',
        headerName: t('pages.participants.table.actions'),
        align: 'right',
        headerAlign: 'right',
        minWidth: 80,
        sortable: false,
        display: 'flex',
        renderCell: function render(
          params: GridRenderCellParams<ParticipantProfile>,
        ) {
          return <ParticipantActions recordItemId={params.row.id} />;
        },
        filterable: false,
      },
    ],
    [t],
  );
};
